'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Settings, X, RotateCcw } from 'lucide-react';
import {
  getMovers, getStocksBatch, searchStocks, getWatchlists, TICKER_TAPE_SPEED_PX_PER_SEC,
  type Mover, type TickerTapeSettings, type TickerTapeSource, type SearchResult,
} from '@/lib/api-client';
import { INDEX_FUND_SYMBOLS } from '@/lib/indexFunds';
import { usePreferences, DEFAULT_TICKER_TAPE_SETTINGS } from '@/context/PreferencesContext';
import { useAuth } from '@/context/AuthContext';
import { useMarketOpen } from '@/hooks/useMarketOpen';
import { cn } from '@/lib/utils';

/** Merge new ticker data into the existing order so refreshes don't jerk
 *  the marquee. Tickers already on screen keep their position (only price
 *  / change% values update); brand-new tickers append at the end; tickers
 *  that dropped out of the source disappear. Without this, every refresh
 *  was reshuffling and visibly snapping the scroll mid-animation. */
function mergePreservingOrder(prev: Mover[], next: Mover[]): Mover[] {
  if (prev.length === 0) return next;
  const byTicker = new Map(next.map((m) => [m.ticker, m]));
  const result: Mover[] = [];
  for (const m of prev) {
    const updated = byTicker.get(m.ticker);
    if (updated) {
      result.push(updated);
      byTicker.delete(m.ticker);
    }
  }
  for (const m of byTicker.values()) result.push(m);
  return result;
}

const TAPE_MAX_TICKERS = 50;

/** Interleave per-source buckets round-robin so 2+ sources mix on screen
 *  instead of running sequentially (plan #227). Output order is
 *  bucketA[0], bucketB[0], bucketC[0], bucketA[1], bucketB[1], ... with
 *  buckets dropping out as they exhaust. Dedups across buckets; caps at
 *  TAPE_MAX_TICKERS. */
function interleaveBuckets(buckets: Mover[][]): Mover[] {
  const seen = new Set<string>();
  const out: Mover[] = [];
  const maxLen = buckets.reduce((m, b) => Math.max(m, b.length), 0);
  for (let i = 0; i < maxLen; i++) {
    for (const b of buckets) {
      if (i >= b.length) continue;
      const m = b[i];
      if (seen.has(m.ticker)) continue;
      seen.add(m.ticker);
      out.push(m);
      if (out.length >= TAPE_MAX_TICKERS) return out;
    }
  }
  return out;
}

async function fetchTopMovers(): Promise<Mover[]> {
  const [gainers, losers] = await Promise.all([
    getMovers('gainers').catch(() => []),
    getMovers('losers').catch(() => []),
  ]);
  const seen = new Set<string>();
  const out: Mover[] = [];
  for (const m of [...gainers.slice(0, 20), ...losers.slice(0, 10)]) {
    if (seen.has(m.ticker)) continue;
    seen.add(m.ticker);
    out.push(m);
  }
  return out;
}

async function fetchBatchAsMovers(tickers: readonly string[]): Promise<Mover[]> {
  if (tickers.length === 0) return [];
  const results = await getStocksBatch(tickers as string[]).catch(() => []);
  return results
    .filter((r) => r.data)
    .map((r) => ({
      ticker: r.ticker,
      name: r.data!.name ?? r.ticker,
      price: r.data!.price ?? 0,
      changePct: r.data!.changePct ?? 0,
      volume: r.data!.volume ?? 0,
      marketCap: r.data!.marketCap ?? 0,
    }));
}

async function fetchPinnedWatchlistMovers(): Promise<Mover[]> {
  const wls = await getWatchlists().catch(() => []);
  const pinned = wls.filter((w) => w.pinned);
  if (pinned.length === 0) return [];
  // Union tickers across all pinned watchlists, dedup.
  const seen = new Set<string>();
  const tickers: string[] = [];
  for (const wl of pinned) {
    for (const it of wl.items) {
      if (seen.has(it.ticker)) continue;
      seen.add(it.ticker);
      tickers.push(it.ticker);
    }
  }
  return fetchBatchAsMovers(tickers);
}

/** Resolve a single source key to its Mover[] bucket. `authed` gates
 *  sources that hit auth-required endpoints (pinnedWatchlist) — without
 *  it, anonymous page-loads emit a console 401 even though the .catch
 *  fallback masks the functional impact. */
async function fetchSourceBucket(src: TickerTapeSource, customTickers: string[], authed: boolean): Promise<Mover[]> {
  switch (src) {
    case 'topMovers':       return fetchTopMovers();
    case 'pinnedWatchlist': return authed ? fetchPinnedWatchlistMovers() : [];
    case 'indexFunds':      return fetchBatchAsMovers(INDEX_FUND_SYMBOLS);
    case 'custom':          return fetchBatchAsMovers(customTickers);
  }
}

const HIDE_ON = ['/login', '/create-account', '/verify-email', '/forgot-password', '/reset-password'];

function TickerTapeImpl() {
  const pathname = usePathname();
  const { tickerTapeSettings, setTickerTapeSettings, tickerVisibility } = usePreferences();
  const { user, loading: authLoading } = useAuth();
  const marketOpen = useMarketOpen();
  const [movers, setMovers] = useState<Mover[]>([]);
  const [showSettings, setShowSettings] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const settingsRef = useRef<HTMLDivElement>(null);
  const hoveredRef = useRef(false);
  const hidden = HIDE_ON.some((p) => pathname === p || pathname.startsWith(p + '/'));

  const effectivelyPaused =
    tickerTapeSettings.scrollBehavior === 'alwaysPause' ||
    (tickerTapeSettings.scrollBehavior === 'marketHoursOnly' && !marketOpen);

  // Close settings on click outside
  useEffect(() => {
    if (!showSettings) return;
    const handler = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setShowSettings(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSettings]);

  // Fetch ticker data (multi-source w/ round-robin interleave — plan #227).
  const sourcesKey = (tickerTapeSettings.sources ?? ['topMovers']).join('|');
  const customKey = tickerTapeSettings.tickers.join(',');
  useEffect(() => {
    if (hidden) return;
    // Wait for AuthContext to finish its initial refresh before firing —
    // otherwise the pinnedWatchlist source's GET /watchlists fires before
    // the access token is set, returning 401 (the .catch fallback works,
    // but the console error is noise). After loading completes, `user` is
    // either set (authed) or null (anon) and fetchSourceBucket can gate
    // appropriately.
    if (authLoading) return;
    let stale = false;
    // First fetch after source/custom change must REPLACE the existing
    // movers (not merge-preserve) — otherwise the prior order pins the
    // already-rendered tickers to the front and any newly-added source
    // gets appended at the end, defeating the round-robin interleave.
    // Subsequent interval refreshes keep using mergePreservingOrder so
    // the marquee doesn't visibly snap on each 5-min refresh.
    let isFirstFetch = true;

    async function fetchData() {
      const sources: TickerTapeSource[] = (tickerTapeSettings.sources && tickerTapeSettings.sources.length > 0)
        ? tickerTapeSettings.sources
        : ['topMovers'];
      const buckets = await Promise.all(
        sources.map((s) => fetchSourceBucket(s, tickerTapeSettings.tickers, user != null)),
      );
      if (stale) return;
      // Multi-source: round-robin interleave so the tape mixes rather than
      // showing each source's block back-to-back. Single source: pass-through.
      // If every bucket is empty (e.g. user enabled only pinnedWatchlist but
      // nothing is pinned), fall back to Top Movers so the tape isn't blank.
      let next: Mover[] = buckets.length === 1
        ? buckets[0].slice(0, TAPE_MAX_TICKERS)
        : interleaveBuckets(buckets);
      if (next.length === 0) {
        next = await fetchTopMovers();
        if (stale) return;
      }
      if (isFirstFetch) {
        isFirstFetch = false;
        setMovers(next);
      } else {
        setMovers((prev) => mergePreservingOrder(prev, next));
      }
    }

    fetchData();
    const id = setInterval(fetchData, 300_000);
    return () => { stale = true; clearInterval(id); };
  }, [hidden, sourcesKey, customKey, authLoading, user]);

  const shouldShow =
    tickerVisibility === 'always' ||
    (tickerVisibility === 'marketHours' && marketOpen);
  // Duplicate items so the marquee fills ultrawide screens (4× covers 4K+).
  // Memoised so identity is stable across non-data re-renders.
  const items = useMemo(() => [...movers, ...movers, ...movers, ...movers], [movers]);

  // RAF-driven scroll. Why not the previous CSS keyframe (`marquee 40s linear
  // infinite` translating from 0 to -25%)?
  //   The CSS approach relied on `translateX(-25%)` exactly equalling one
  //   copy width — true in theory because the inner element width is 4
  //   copies, but a single sub-pixel rounding difference (which the user
  //   absolutely sees on high-DPI displays) means the keyframe end-of-cycle
  //   snap leaves the marquee a fraction of a pixel off, so AMZN appears to
  //   "jump back" each cycle.
  //   RAF + exact pixel measurement is bulletproof: pos = (pos + dt*speed) %
  //   copyWidth means the wrap is mathematically perfect regardless of layout.
  // Hover/pause/visibility gates handled in-effect so we never run the RAF
  // off-screen or while the user is reading a single ticker.
  const animActive = !effectivelyPaused && shouldShow && !hidden && movers.length > 0;
  // Live-read the speed inside the RAF tick via a ref so flipping
  // slower/normal/faster doesn't tear down the loop and reset position.
  const speed = tickerTapeSettings.speed ?? 'normal';
  const speedPxPerSecRef = useRef(TICKER_TAPE_SPEED_PX_PER_SEC[speed]);
  useEffect(() => {
    speedPxPerSecRef.current = TICKER_TAPE_SPEED_PX_PER_SEC[speed];
  }, [speed]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (!animActive) {
      el.style.transform = 'translate3d(0, 0, 0)';
      return;
    }

    let raf = 0;
    let pos = 0;
    let prev = performance.now();

    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - prev) / 1000); // clamp so a tab-switch resume doesn't jump
      prev = now;
      // 4 copies → one copy = scrollWidth / 4. Re-read every frame so a
      // 5-min refresh that adds a digit doesn't desync the wrap.
      const oneCopy = el.scrollWidth / 4;
      if (oneCopy > 0 && !hoveredRef.current) {
        pos = (pos + speedPxPerSecRef.current * dt) % oneCopy;
        el.style.transform = `translate3d(${-pos}px, 0, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [animActive]);

  if (hidden || !shouldShow || movers.length === 0) return null;

  // Sticky positioning: pin the tape below the 56px Navbar so it stays
  // visible as the page scrolls. shadow-md adds a subtle separation from
  // the page content beneath when frozen.
  const freezeOnScroll = !!tickerTapeSettings.freezeOnScroll;

  return (
    <div className={cn(
      'hidden md:flex ticker-tape-bg border-b border-card-bg items-center',
      freezeOnScroll && 'sticky top-14 z-40 shadow-md shadow-black/20',
    )}>
      <div className="flex-1 overflow-hidden">
        <div
          ref={scrollRef}
          onMouseEnter={() => { hoveredRef.current = true; }}
          onMouseLeave={() => { hoveredRef.current = false; }}
          style={{ willChange: 'transform' }}
          className="flex whitespace-nowrap"
        >
          {items.map((m, i) => (
            <Link
              key={`${m.ticker}-${i}`}
              href={`/stocks/${m.ticker}`}
              className="inline-flex items-center gap-2 px-4 py-1.5 text-xs ticker-tape-hover transition-colors flex-shrink-0"
            >
              <span className="font-semibold ticker-tape-ticker">{m.ticker}</span>
              {m.price > 0 && (
                <>
                  {/* min-width reserves space so the 5-min refresh that swaps
                      "999.99" for "1,000.00" (extra digit + comma) doesn't
                      reflow the marquee mid-animation. tabular-nums handles
                      digit widths; min-w covers variable char counts (sign
                      flip, threshold cross, +9.99% to +99.99%). */}
                  <span className="ticker-tape-price tabular-nums inline-block text-right min-w-[5.5ch]">{m.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span className={cn(
                    'font-medium tabular-nums inline-block text-right min-w-[7ch]',
                    m.changePct >= 0 ? 'text-emerald-500' : 'text-red-500'
                  )}>
                    {m.changePct >= 0 ? '\u25B2' : '\u25BC'} {m.changePct >= 0 ? '+' : ''}{m.changePct.toFixed(2)}%
                  </span>
                </>
              )}
            </Link>
          ))}
        </div>
      </div>
      <div className="relative flex-shrink-0" ref={settingsRef}>
        <button
          onClick={() => setShowSettings((p) => !p)}
          className="px-2.5 py-1.5 text-fg-subtle hover:text-fg transition-colors border-l border-card-bg"
          aria-label="Ticker settings"
          title="Ticker settings"
        >
          <Settings size={12} />
        </button>
        {showSettings && (
          <TickerSettingsPopup
            settings={tickerTapeSettings}
            onChange={setTickerTapeSettings}
            onClose={() => setShowSettings(false)}
          />
        )}
      </div>
    </div>
  );
}

// React.memo guards against parent (LayoutShell, Navbar tree) re-renders
// cascading into a full TickerTape re-render. Without it, anything in the
// shell that re-renders cheaply still pulls TickerTape with it, and the
// items array + className get re-evaluated each tick — enough for the
// browser to drop a frame on the marquee. The component takes no props,
// so the default shallow-equality comparison is exactly right.
const TickerTape = React.memo(TickerTapeImpl);
export default TickerTape;

// ── Settings Popup ────────────────────────────────────────────────────────────

function TickerSettingsPopup({
  settings,
  onChange,
  onClose,
}: {
  settings: TickerTapeSettings;
  onChange: (s: TickerTapeSettings) => void;
  onClose: () => void;
}) {
  const update = (partial: Partial<TickerTapeSettings>) => onChange({ ...settings, ...partial });

  return (
    <div
      className="absolute right-0 top-full mt-1 z-50 w-80 bg-card-bg border border-default rounded-xl shadow-2xl p-4 text-sm"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="font-semibold text-slate-200">Ticker Settings</span>
        <button onClick={onClose} className="text-fg-subtle hover:text-fg transition-colors">
          <X size={14} />
        </button>
      </div>

      {/* Ticker Source — multi-select (plan #227). Any non-empty subset.
          Empty subset falls back to topMovers in the consumer. */}
      <div className="mb-4">
        <label className="text-xs text-fg-muted uppercase tracking-wide font-medium mb-2 block">Source</label>
        <div className="grid grid-cols-2 gap-1.5">
          {([
            ['topMovers',       'Top Movers'],
            ['pinnedWatchlist', 'Pinned Watchlist'],
            ['indexFunds',      'Index Funds'],
            ['custom',          'Custom'],
          ] as const).map(([value, label]) => {
            const sources = settings.sources ?? [];
            const checked = sources.includes(value);
            return (
              <button
                key={value}
                onClick={() => {
                  // Toggle membership while preserving insertion order so the
                  // round-robin interleave priority matches click order.
                  const nextSources = checked
                    ? sources.filter((s) => s !== value)
                    : [...sources, value];
                  update({ sources: nextSources, mode: undefined });
                }}
                className={cn(
                  'flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border text-left',
                  checked
                    ? 'bg-blue-600/20 border-blue-500/50 text-blue-300'
                    : 'border-default text-fg-muted hover:text-fg hover:border-muted',
                )}
              >
                <span className={cn(
                  'w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0',
                  checked ? 'bg-blue-500 border-blue-500' : 'border-slate-500',
                )}>
                  {checked && <span className="block w-1.5 h-1.5 bg-white rounded-sm" />}
                </span>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom Ticker List */}
      {(settings.sources ?? []).includes('custom') && (
        <div className="mb-4">
          <label className="text-xs text-fg-muted uppercase tracking-wide font-medium mb-2 block">
            Tickers ({settings.tickers.length}/20)
          </label>
          <div className="flex flex-wrap gap-1.5 mb-2 max-h-24 overflow-y-auto">
            {settings.tickers.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-surface border border-default text-xs text-slate-300"
              >
                {t}
                <button
                  onClick={() => update({ tickers: settings.tickers.filter((x) => x !== t) })}
                  className="text-fg-subtle hover:text-red-400 transition-colors"
                >
                  <X size={10} />
                </button>
              </span>
            ))}
            {settings.tickers.length === 0 && (
              <span className="text-xs text-fg-subtle italic">No tickers added</span>
            )}
          </div>
          {settings.tickers.length < 20 && (
            <TickerSearchInput
              onAdd={(ticker) => {
                if (!settings.tickers.includes(ticker)) {
                  update({ tickers: [...settings.tickers, ticker] });
                }
              }}
            />
          )}
        </div>
      )}

      {/* Scroll Behavior */}
      <div className="mb-4">
        <label className="text-xs text-fg-muted uppercase tracking-wide font-medium mb-2 block">Scroll Behavior</label>
        <div className="space-y-1.5">
          {([
            ['alwaysPlay', 'Always scroll'],
            ['alwaysPause', 'Always paused'],
            ['marketHoursOnly', 'Market hours only'],
          ] as const).map(([value, label]) => (
            <label
              key={value}
              className={cn(
                'flex items-center gap-2.5 px-3 py-1.5 rounded-lg cursor-pointer transition-colors border',
                settings.scrollBehavior === value
                  ? 'bg-blue-600/20 border-blue-500/50'
                  : 'border-transparent hover:bg-surface'
              )}
            >
              <input
                type="radio"
                name="scrollBehavior"
                value={value}
                checked={settings.scrollBehavior === value}
                onChange={() => update({ scrollBehavior: value })}
                className="sr-only"
              />
              <div className={cn(
                'w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                settings.scrollBehavior === value ? 'border-blue-500' : 'border-slate-500'
              )}>
                {settings.scrollBehavior === value && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
              </div>
              <div>
                <span className={cn('text-xs', settings.scrollBehavior === value ? 'text-blue-400 font-medium' : 'text-slate-300')}>
                  {label}
                </span>
                {value === 'marketHoursOnly' && (
                  <span className="block text-[10px] text-fg-subtle mt-0.5">9:30 AM – 4:00 PM ET, Mon–Fri</span>
                )}
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Speed */}
      <div className="mb-4">
        <label className="text-xs text-fg-muted uppercase tracking-wide font-medium mb-2 block">Speed</label>
        <div className="flex rounded-lg overflow-hidden border border-default">
          {([
            ['slower', 'Slower'],
            ['normal', 'Normal'],
            ['faster', 'Faster'],
          ] as const).map(([value, label]) => {
            const active = (settings.speed ?? 'normal') === value;
            return (
              <button
                key={value}
                onClick={() => update({ speed: value })}
                className={cn(
                  'flex-1 px-3 py-1.5 text-xs font-medium transition-colors',
                  active ? 'bg-blue-600/20 text-blue-400' : 'bg-transparent text-fg-muted hover:text-fg',
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Freeze on scroll */}
      <div className="mb-4">
        <label className="flex items-start gap-2.5 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-surface transition-colors">
          <input
            type="checkbox"
            checked={!!settings.freezeOnScroll}
            onChange={(e) => update({ freezeOnScroll: e.target.checked })}
            className="mt-0.5 w-3.5 h-3.5 rounded border-slate-500 bg-surface text-blue-500 focus:ring-blue-500/50"
          />
          <span className="text-xs text-slate-300">Keep visible while scrolling</span>
        </label>
      </div>

      {/* Reset */}
      <button
        onClick={() => onChange({ ...DEFAULT_TICKER_TAPE_SETTINGS })}
        className="flex items-center gap-1.5 text-xs text-fg-subtle hover:text-fg transition-colors"
      >
        <RotateCcw size={11} />
        Reset to defaults
      </button>
    </div>
  );
}

// ── Ticker Search Input ───────────────────────────────────────────────────────

function TickerSearchInput({ onAdd }: { onAdd: (ticker: string) => void }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!query.trim()) { setSuggestions([]); setOpen(false); return; }
    const timer = setTimeout(() => {
      searchStocks(query)
        .then((results) => {
          setSuggestions(results.slice(0, 6));
          setOpen(results.length > 0);
        })
        .catch(() => {});
    }, 200);
    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (ticker: string) => {
    onAdd(ticker);
    setQuery('');
    setSuggestions([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && suggestions.length > 0) {
            e.preventDefault();
            handleSelect(suggestions[0].ticker);
          }
        }}
        placeholder="Search ticker..."
        className="w-full px-3 py-1.5 text-xs bg-surface border border-default rounded-lg text-slate-300 placeholder-slate-500 outline-none focus:border-blue-500/50"
      />
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-surface border border-default rounded-lg shadow-xl overflow-hidden z-10 max-h-48 overflow-y-auto">
          {suggestions.map((s) => (
            <button
              key={s.ticker}
              onClick={() => handleSelect(s.ticker)}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-card-bg transition-colors flex items-center gap-2"
            >
              <span className="font-semibold text-blue-400">{s.ticker}</span>
              <span className="text-fg-subtle truncate">{s.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
